import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import MillosLogo from '../components/MillosLogo';

type State = 'checking' | 'verified' | 'failed';

// Opened from the link in the welcome email. The token is only sent when this page loads
// (not on a plain GET), so mail scanners that pre-fetch links can't verify an address by accident.
const VerifyEmailPage: React.FC = () => {
  const { palette } = useTheme();
  const t = palette.tokens;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [state, setState] = useState<State>('checking');
  const [error, setError] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = params.get('token') || '';
    axios.post(`${config.apiUrl}/api/verify-email`, { token })
      .then(() => { setState('verified'); refreshUser(); })
      .catch(err => {
        setError(err.response?.data?.error || 'We could not verify your email. Please try again.');
        setState('failed');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: t.canvas, p: 2 }}>
      <Card sx={{ maxWidth: 440, width: '100%', borderRadius: '16px', border: `1px solid ${t.hairline}`, boxShadow: 'none' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <MillosLogo size={48} />
          </Box>

          {state === 'checking' && (
            <>
              <CircularProgress size={28} sx={{ mb: 2 }} />
              <Typography sx={{ color: t.muted }}>Verifying your email</Typography>
            </>
          )}

          {state === 'verified' && (
            <>
              <CheckCircleOutlineIcon aria-hidden sx={{ fontSize: 48, color: t.success, mb: 1 }} />
              <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 700, color: t.ink, mb: 1 }}>
                Email verified
              </Typography>
              <Typography sx={{ color: t.muted, mb: 3 }}>
                Thanks, you're all set. You can close this tab and go back to the app.
              </Typography>
              <Button
                variant="outlined"
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                {user ? 'Go to the app' : 'Sign in'}
              </Button>
            </>
          )}

          {state === 'failed' && (
            <>
              <Typography component="h1" sx={{ fontSize: '1.5rem', fontWeight: 700, color: t.ink, mb: 2 }}>
                We couldn't verify your email
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert>
              <Button variant="outlined" onClick={() => navigate(user ? '/dashboard' : '/login')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                {user ? 'Go to the app' : 'Sign in'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyEmailPage;
